import { useContext, useEffect, useState } from 'react'

// others
import { useSearchParams } from 'react-router-dom'
import { Department, PaginationPayload, useFetchAllDepartmentsLazyQuery } from '../../generated'
import { GRAPHQL_QUERY_POLICY, PAGE_LIMIT, QUERY_PARAMS } from '../constants'
import { AppContext } from '../context'
import { ActionType } from '../context/AppContextReducer'
import { useDebounce } from 'use-debounce'

export const useDepartments = ({ listingPage = false }: { listingPage?: boolean }) => {
  const [departments, setDepartments] = useState<Department[]>([])
  const [pagination, setPagination] = useState<PaginationPayload | null>({})
  const [page, setPage] = useState(1)
  const [hasMore, setHasMore] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [searchParams] = useSearchParams()

  const [selectedDepartments, setSelectedDepartments] = useState<Department[]>([])

  const lawTypes = searchParams.get(QUERY_PARAMS.LAW_TYPES)

  const { dispatch: reducerDispatch, searchValue, currentPage } = useContext(AppContext)
  const [debouncedValue] = useDebounce(listingPage ? searchValue : searchTerm, 500)

  const [fetchAllDepartments, { loading, refetch }] = useFetchAllDepartmentsLazyQuery({
    ...GRAPHQL_QUERY_POLICY,
    variables: undefined,
    onError: error => {
      console.log(error)
      setDepartments([])
      setHasMore(false)
      reducerDispatch({ type: ActionType.SET_IS_LOADING, isLoading: false })
    },
    onCompleted: data => {
      const { fetchAllDepartments } = data ?? {}
      const { departments = [], pagination = {} } = fetchAllDepartments ?? {}
      const { page: currentPageNo, totalPages } = pagination || {}

      const newHasMore = currentPageNo && totalPages ? currentPageNo < totalPages : false

      setDepartments(prev => {
        const newDepartments = page !== 1 ? [...prev, ...(departments || [])] : departments

        const mergedDepartments = mergeDepartments(newDepartments as Department[], selectedDepartments)

        return mergedDepartments
      })
      setHasMore(newHasMore)
      setPagination(pagination)
    }
  })

  // Helper function to merge departments while avoiding duplicates
  const mergeDepartments = (fetchedDepartments: Department[], selectedDepts: Department[]): Department[] => {
    const fetchedIds = new Set(fetchedDepartments.map(dept => dept.id))
    const uniqueSelectedDepts = selectedDepts.filter(dept => !fetchedIds.has(dept.id))

    if (debouncedValue) {
      return [...uniqueSelectedDepts, ...fetchedDepartments]
    }

    return [...fetchedDepartments, ...uniqueSelectedDepts]
  }

  const addSelectedDepartments = (deptIds: string[]) => {
    const currentDeptIds = selectedDepartments.map(dept => dept.id)
    const newDeptIds = deptIds.filter(id => !currentDeptIds.includes(id))

    if (newDeptIds.length > 0) {
      const newSelectedDepts = departments.filter(dept => newDeptIds.includes(dept.id))
      setSelectedDepartments(prev => [...prev, ...newSelectedDepts])
    }
  }

  // A department created inline isn't in the fetched page yet; pin it so later page merges keep it.
  const addCreatedDepartment = (department: Department) => {
    setSelectedDepartments(prev => [...prev, department])
    setDepartments(prev => [department, ...prev.filter(dept => dept.id !== department.id)])
  }

  const removeSelectedDepartments = (deptIds: string[]) => {
    setSelectedDepartments(prev => prev.filter(dept => !deptIds.includes(dept.id)))
  }

  useEffect(() => {
    setPage(1)
    setHasMore(true)

    if (page === 1) {
      setDepartments([])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedValue, lawTypes])

  useEffect(() => {
    if (hasMore || listingPage) {
      fetchAllDepartments({
        variables: {
          fetchDepartmentsInput: {
            paginationOptions: {
              limit: PAGE_LIMIT,
              page: listingPage ? currentPage : page
            },
            departmentFilters: {
              lawTypeIds: lawTypes ? lawTypes.split(',') : undefined
            },
            keyword: debouncedValue
          }
        }
      })
    }
  }, [fetchAllDepartments, debouncedValue, lawTypes, page, hasMore, currentPage, listingPage])

  return {
    departments,
    pagination,
    loading,
    refetch,
    page,
    setPage,
    searchTerm,
    setSearchTerm,
    hasMore,
    setDepartments,
    addSelectedDepartments,
    addCreatedDepartment,
    removeSelectedDepartments
  }
}
